import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

export const HANDLE_MIN_LENGTH = 3;
export const HANDLE_MAX_LENGTH = 30;

export const normalizeHandle = (handle: string) => handle.trim().toLowerCase();

/**
 * A people-search term reduced to what the backend matches on: trimmed, with a leading "@"
 * dropped (the server tolerates it either way). Shared so the query, the People results and the
 * chat picker all agree on when a term is searchable and when it has changed.
 */
export const normalizeHandleSearchTerm = (term: string) =>
  term.trim().replace(/^@/, "");

/**
 * Mirrors the backend HandlePolicy (Domain/App/Services/HandlePolicy.cs): 3-30 chars of
 * a-z / 0-9 / '.' / '_', at least one letter (all-digit handles would be shadowed by
 * phone-shaped search), no leading/trailing dot. Expects a normalized value.
 */
export const isValidHandle = (handle: string) =>
  /^[a-z0-9_.]{3,30}$/.test(handle) &&
  /[a-z]/.test(handle) &&
  !handle.startsWith(".") &&
  !handle.endsWith(".");

/**
 * Keep in sync with HandlePolicy's reserved list — the backend is the authority; this
 * mirror only exists so reserved handles fail client-side with a specific message
 * instead of surfacing as a server conflict on submit.
 */
const RESERVED_HANDLES: ReadonlyArray<string> = [
  "admin",
  "administrator",
  "help",
  "info",
  "mo.one",
  "moone",
  "official",
  "payment",
  "payments",
  "root",
  "support",
  "system",
];

export const isReservedHandle = (handle: string) =>
  RESERVED_HANDLES.includes(handle);

/**
 * The single-field zod chain — embed into any host form (wizard steps) that collects a
 * handle alongside other data; `getHandleFormSchema` wraps it for the standalone form.
 */
export const getHandleFieldSchema = (t: TFunction) =>
  z
    .string()
    .transform(normalizeHandle)
    .refine(isValidHandle, t(Resources.Social.Handle.ValidationError))
    .refine(
      (handle) => !isReservedHandle(handle),
      t(Resources.Social.Handle.Reserved),
    );

type HandleFormSchemaOptions = {
  /**
   * The persona has no handle yet, so this submit doubles as consent to the social-features terms
   * (ZS-1749) — the checkbox must be ticked. A later handle change needs no re-consent.
   */
  isSocialTermsConsentRequired: boolean;
};

export const getHandleFormSchema = (
  t: TFunction,
  { isSocialTermsConsentRequired }: HandleFormSchemaOptions,
) =>
  z.object({
    handle: getHandleFieldSchema(t),
    acceptSocialTerms: isSocialTermsConsentRequired
      ? z.boolean().refine((value) => value, t(Resources.Social.Terms.Required))
      : z.boolean(),
  });

export type HandleFormModel = z.infer<ReturnType<typeof getHandleFormSchema>>;
